import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import {
  BadgeCheck,
  Crown,
  Grip,
  RotateCcw,
  Activity,
  ArrowUpRight,
  Dumbbell,
  X,
  Save,
  Shield,
  Sparkles,
  UserCheck,
  UserPlus,
  Move,
  Users,
} from 'lucide-react';
import { POLONIA_CREST } from './clubIdentity';
import { useData } from './DataContext';
import { useI18n } from './I18nContext';
import { playerStats, sortPlayers } from './playerUtils';
import { MatchdayMediaStudio } from './MatchdayMediaStudio';
import type { Lineup, Player } from './types';
import { canViewSensitiveIntelligence } from './staffAccess';

type FormationDefinition = {
  label: string;
  rows: Array<Array<string>>;
};

type TacticalPreset = {
  id: string;
  labelKey: 'balancedPreset' | 'highPressPreset' | 'lowBlockPreset' | 'directPreset';
  formation: string;
  descriptionKey: 'balancedPresetDesc' | 'highPressPresetDesc' | 'lowBlockPresetDesc' | 'directPresetDesc';
};

type LineupMeta = {
  captainId: string;
  viceCaptainId: string;
  announced: boolean;
};

const FORMATIONS: Record<string, FormationDefinition> = {
  '4-3-3': {
    label: '4-3-3',
    rows: [['LW', 'ST', 'RW'], ['LCM', 'CM', 'RCM'], ['LB', 'LCB', 'RCB', 'RB'], ['GK']],
  },
  '4-2-3-1': {
    label: '4-2-3-1',
    rows: [['ST'], ['LW', 'CAM', 'RW'], ['LDM', 'RDM'], ['LB', 'LCB', 'RCB', 'RB'], ['GK']],
  },
  '4-4-2': {
    label: '4-4-2',
    rows: [['LST', 'RST'], ['LM', 'LCM', 'RCM', 'RM'], ['LB', 'LCB', 'RCB', 'RB'], ['GK']],
  },
  '3-5-2': {
    label: '3-5-2',
    rows: [['LST', 'RST'], ['LWB', 'LCM', 'CAM', 'RCM', 'RWB'], ['LCB', 'CB', 'RCB'], ['GK']],
  },
  '3-4-2-1': {
    label: '3-4-2-1',
    rows: [['ST'], ['LAM', 'RAM'], ['LWB', 'LCM', 'RCM', 'RWB'], ['LCB', 'CB', 'RCB'], ['GK']],
  },
  '4-1-4-1': {
    label: '4-1-4-1',
    rows: [['ST'], ['LM', 'LCM', 'RCM', 'RM'], ['CDM'], ['LB', 'LCB', 'RCB', 'RB'], ['GK']],
  },
  '5-2-3': {
    label: '5-2-3',
    rows: [['LW', 'ST', 'RW'], ['LCM', 'RCM'], ['LWB', 'LCB', 'CB', 'RCB', 'RWB'], ['GK']],
  },
  '4-1-2-1-2': {
    label: '4-1-2-1-2',
    rows: [['LST', 'RST'], ['CAM'], ['LCM', 'RCM'], ['CDM'], ['LB', 'LCB', 'RCB', 'RB'], ['GK']],
  },
};



const FORMATION_WORLD_COORDS: Record<string, Record<string, { x: number; z: number }>> = {
  '4-3-3': {
    LW:{x:-0.82,z:0.16}, ST:{x:0,z:0.10}, RW:{x:0.82,z:0.16},
    LCM:{x:-0.49,z:0.43}, CM:{x:0,z:0.49}, RCM:{x:0.49,z:0.43},
    LB:{x:-0.86,z:0.70}, LCB:{x:-0.33,z:0.67}, RCB:{x:0.33,z:0.67}, RB:{x:0.86,z:0.70},
    GK:{x:0,z:0.92},
  },
  '4-2-3-1': {
    ST:{x:0,z:0.10},
    LW:{x:-0.80,z:0.34}, CAM:{x:0,z:0.31}, RW:{x:0.80,z:0.34},
    LDM:{x:-0.34,z:0.55}, RDM:{x:0.34,z:0.55},
    LB:{x:-0.86,z:0.72}, LCB:{x:-0.33,z:0.68}, RCB:{x:0.33,z:0.68}, RB:{x:0.86,z:0.72},
    GK:{x:0,z:0.92},
  },
  '4-4-2': {
    LST:{x:-0.31,z:0.12}, RST:{x:0.31,z:0.12},
    LM:{x:-0.86,z:0.43}, LCM:{x:-0.31,z:0.47}, RCM:{x:0.31,z:0.47}, RM:{x:0.86,z:0.43},
    LB:{x:-0.86,z:0.71}, LCB:{x:-0.33,z:0.67}, RCB:{x:0.33,z:0.67}, RB:{x:0.86,z:0.71},
    GK:{x:0,z:0.92},
  },
  '3-5-2': {
    LST:{x:-0.31,z:0.12}, RST:{x:0.31,z:0.12},
    LWB:{x:-0.93,z:0.45}, LCM:{x:-0.42,z:0.49}, CAM:{x:0,z:0.38}, RCM:{x:0.42,z:0.49}, RWB:{x:0.93,z:0.45},
    LCB:{x:-0.55,z:0.70}, CB:{x:0,z:0.66}, RCB:{x:0.55,z:0.70},
    GK:{x:0,z:0.92},
  },
  '3-4-2-1': {
    ST:{x:0,z:0.10},
    LAM:{x:-0.35,z:0.31}, RAM:{x:0.35,z:0.31},
    LWB:{x:-0.90,z:0.53}, LCM:{x:-0.31,z:0.55}, RCM:{x:0.31,z:0.55}, RWB:{x:0.90,z:0.53},
    LCB:{x:-0.55,z:0.72}, CB:{x:0,z:0.68}, RCB:{x:0.55,z:0.72},
    GK:{x:0,z:0.92},
  },
  '4-1-4-1': {
    ST:{x:0,z:0.11},
    LM:{x:-0.86,z:0.42}, LCM:{x:-0.34,z:0.47}, RCM:{x:0.34,z:0.47}, RM:{x:0.86,z:0.42},
    CDM:{x:0,z:0.59},
    LB:{x:-0.86,z:0.75}, LCB:{x:-0.33,z:0.70}, RCB:{x:0.33,z:0.70}, RB:{x:0.86,z:0.75},
    GK:{x:0,z:0.92},
  },
  '5-2-3': {
    LW:{x:-0.80,z:0.15}, ST:{x:0,z:0.10}, RW:{x:0.80,z:0.15},
    LCM:{x:-0.31,z:0.48}, RCM:{x:0.31,z:0.48},
    LWB:{x:-0.97,z:0.70}, LCB:{x:-0.55,z:0.67}, CB:{x:0,z:0.64}, RCB:{x:0.55,z:0.67}, RWB:{x:0.97,z:0.70},
    GK:{x:0,z:0.92},
  },
  '4-1-2-1-2': {
    LST:{x:-0.29,z:0.11}, RST:{x:0.29,z:0.11},
    CAM:{x:0,z:0.34},
    LCM:{x:-0.35,z:0.50}, RCM:{x:0.35,z:0.50},
    CDM:{x:0,z:0.62},
    LB:{x:-0.86,z:0.77}, LCB:{x:-0.33,z:0.72}, RCB:{x:0.33,z:0.72}, RB:{x:0.86,z:0.77},
    GK:{x:0,z:0.93},
  },
};

function projectWorldPoint(worldX: number, worldZ: number) {
  const z = Math.max(0, Math.min(1, worldZ));
  // Cards must follow the perspective width of the painted pitch. The far end
  // is narrower than the near end, so wide players no longer sit in the stand.
  const usableHalfWidth = 27.5 + z * 11.5;
  const x = 50 + worldX * usableHalfWidth;
  const y = 27 + z * 62.5;
  const scale = 0.96 + z * 0.035;
  return {
    x: Number(x.toFixed(2)),
    y: Number(y.toFixed(2)),
    scale: Number(scale.toFixed(3)),
    rotate: 0,
    shadow: Number((0.18 + z * 0.08).toFixed(3)),
  };
}

function formationRowY(rowIndex: number, rowCount: number) {
  const anchors: Record<number, number[]> = {
    4: [28.5, 49.0, 69.5, 87.0],
    5: [27.5, 42.5, 57.5, 72.5, 88.0],
    6: [26.5, 38.5, 50.5, 62.5, 74.5, 88.5],
  };
  const preset = anchors[rowCount];
  if (preset?.[rowIndex] != null) return preset[rowIndex];
  if (rowCount <= 1) return 50;
  return 28 + (rowIndex / (rowCount - 1)) * 60;
}

const FORMATION_COORDINATES: Record<string, Record<string, { x: number; y: number }>> = {
  '4-3-3': { LW:{x:23,y:31}, ST:{x:50,y:27}, RW:{x:77,y:31}, LCM:{x:26,y:49}, CM:{x:50,y:52}, RCM:{x:74,y:49}, LB:{x:13,y:69}, LCB:{x:38,y:66}, RCB:{x:62,y:66}, RB:{x:87,y:69}, GK:{x:50,y:86} },
  '4-2-3-1': { ST:{x:50,y:27}, LW:{x:23,y:42}, CAM:{x:50,y:39}, RW:{x:77,y:42}, LDM:{x:38,y:57}, RDM:{x:62,y:57}, LB:{x:13,y:72}, LCB:{x:38,y:68}, RCB:{x:62,y:68}, RB:{x:87,y:72}, GK:{x:50,y:87} },
  '4-4-2': { LST:{x:38,y:29}, RST:{x:62,y:29}, LM:{x:15,y:49}, LCM:{x:39,y:51}, RCM:{x:61,y:51}, RM:{x:85,y:49}, LB:{x:13,y:70}, LCB:{x:38,y:67}, RCB:{x:62,y:67}, RB:{x:87,y:70}, GK:{x:50,y:87} },
  '3-5-2': { LST:{x:38,y:29}, RST:{x:62,y:29}, LWB:{x:10,y:49}, LCM:{x:33,y:52}, CAM:{x:50,y:45}, RCM:{x:67,y:52}, RWB:{x:90,y:49}, LCB:{x:28,y:70}, CB:{x:50,y:67}, RCB:{x:72,y:70}, GK:{x:50,y:87} },
  '3-4-2-1': { ST:{x:50,y:27}, LAM:{x:37,y:41}, RAM:{x:63,y:41}, LWB:{x:12,y:56}, LCM:{x:38,y:58}, RCM:{x:62,y:58}, RWB:{x:88,y:56}, LCB:{x:28,y:72}, CB:{x:50,y:69}, RCB:{x:72,y:72}, GK:{x:50,y:88} },
  '4-1-4-1': { ST:{x:50,y:27}, LM:{x:15,y:47}, LCM:{x:38,y:50}, RCM:{x:62,y:50}, RM:{x:85,y:47}, CDM:{x:50,y:61}, LB:{x:13,y:74}, LCB:{x:38,y:70}, RCB:{x:62,y:70}, RB:{x:87,y:74}, GK:{x:50,y:89} },
  '5-2-3': { LW:{x:23,y:30}, ST:{x:50,y:27}, RW:{x:77,y:30}, LCM:{x:39,y:50}, RCM:{x:61,y:50}, LWB:{x:8,y:70}, LCB:{x:29,y:67}, CB:{x:50,y:65}, RCB:{x:71,y:67}, RWB:{x:92,y:70}, GK:{x:50,y:88} },
  '4-1-2-1-2': { LST:{x:39,y:28}, RST:{x:61,y:28}, CAM:{x:50,y:42}, LCM:{x:38,y:54}, RCM:{x:62,y:54}, CDM:{x:50,y:64}, LB:{x:13,y:76}, LCB:{x:38,y:72}, RCB:{x:62,y:72}, RB:{x:87,y:76}, GK:{x:50,y:89} },
};

const PRESETS: TacticalPreset[] = [
  { id: 'balanced', labelKey: 'balancedPreset', formation: '4-3-3', descriptionKey: 'balancedPresetDesc' },
  { id: 'press', labelKey: 'highPressPreset', formation: '4-2-3-1', descriptionKey: 'highPressPresetDesc' },
  { id: 'block', labelKey: 'lowBlockPreset', formation: '4-4-2', descriptionKey: 'lowBlockPresetDesc' },
  { id: 'direct', labelKey: 'directPreset', formation: '3-4-2-1', descriptionKey: 'directPresetDesc' },
];

function playerOverall(player: Player) {
  const attributeValues = Object.values(player.attributes || {}).filter((value) => Number.isFinite(value));
  const attributeAverage = attributeValues.length
    ? attributeValues.reduce((sum, value) => sum + Number(value), 0) / attributeValues.length
    : Number(player.current_form ?? 60);
  const form = Number(player.current_form ?? attributeAverage);
  const fitness = Number(player.fitness ?? 75);
  const morale = Number(player.morale ?? 75);
  return Math.max(1, Math.min(99, Math.round(
    attributeAverage * .58 + form * .22 + fitness * .12 + morale * .08,
  )));
}


function normalizedRole(value?: string | null) {
  const role = String(value || '').toUpperCase().replace(/[\s_-]+/g, '');
  if (role.includes('GK')) return 'GK';
  if (role.includes('FBL') || role === 'LB' || role.includes('LWB')) return 'LB';
  if (role.includes('FBR') || role === 'RB' || role.includes('RWB')) return 'RB';
  if (role.includes('CB')) return 'CB';
  if (role.includes('DMF') || role.includes('CDM') || role === 'DM') return 'DM';
  if (role.includes('AMF') || role.includes('CAM') || role === 'AM') return 'AM';
  if (role.includes('CMF') || role === 'CM' || role.includes('LCM') || role.includes('RCM')) return 'CM';
  if (role.includes('WINGERL') || role === 'LW' || role === 'LM') return 'LW';
  if (role.includes('WINGERR') || role === 'RW' || role === 'RM') return 'RW';
  if (role.includes('ST') || role.includes('CF')) return 'ST';
  return role || 'TBA';
}

function targetRole(slot: string) {
  const value = slot.toUpperCase();
  if (value.includes('GK')) return 'GK';
  if (value.includes('LB') || value.includes('LWB')) return 'LB';
  if (value.includes('RB') || value.includes('RWB')) return 'RB';
  if (value.includes('CB')) return 'CB';
  if (value.includes('DM')) return 'DM';
  if (value.includes('AM')) return 'AM';
  if (value.includes('CM')) return 'CM';
  if (value.includes('LW') || value === 'LM') return 'LW';
  if (value.includes('RW') || value === 'RM') return 'RW';
  if (value.includes('ST') || value.includes('CF')) return 'ST';
  return value;
}

function roleAffinity(player: Player, slot: string) {
  const playerRole = normalizedRole(player.position);
  const target = targetRole(slot);
  if (playerRole === target) return 28;
  const compatible: Record<string, string[]> = {
    GK: [], CB: ['LB','RB','DM'], LB: ['CB','LW'], RB: ['CB','RW'],
    DM: ['CM','CB'], CM: ['DM','AM'], AM: ['CM','LW','RW','ST'],
    LW: ['AM','ST','LB'], RW: ['AM','ST','RB'], ST: ['AM','LW','RW'],
  };
  if ((compatible[target] || []).includes(playerRole)) return 12;
  return 0;
}

function metaStorageKey(teamId: string, lineupName: string) {
  return `ecc_lineup_meta_${teamId}_${lineupName.trim().toLowerCase().replace(/\s+/g, '-')}`;
}

function draftStorageKey(teamId: string) {
  return `ecc_lineup_draft_${teamId}`;
}

type LocalLineupDraft = {
  formation: string;
  name: string;
  slots: Record<string, string | null>;
  captainId: string;
  viceCaptainId: string;
  announced: boolean;
  savedAt: string;
};


function positionVisualProfile(slot: string, x: number, y: number) {
  const depth = Math.max(0, Math.min(1, (y - 24) / 66));
  const baseScale = 0.88 + depth * 0.20;

  let rotation = 0;
  if (x < 35) rotation = -3.5;
  else if (x > 65) rotation = 3.5;

  let lift = -2;
  if (slot === 'GK') lift = 2;
  else if (slot.includes('CB') || slot === 'CB') lift = 0;
  else if (slot.includes('ST') || slot === 'ST') lift = -4;

  return {
    scale: Number(baseScale.toFixed(3)),
    rotation,
    lift,
    shadow: 0.18 + depth * 0.22,
  };
}

export function LineupBuilder() {
  const { t, language } = useI18n();
  const { team, players, matches, relationships, lineups, loading, error, saveLineup, publishLineup } = useData();

  const [formation, setFormation] = useState('4-3-3');
  const [name, setName] = useState('Matchday XI');
  const [slots, setSlots] = useState<Record<string, string | null>>({});
  const [message, setMessage] = useState('');
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [quickViewPlayerId, setQuickViewPlayerId] = useState('');
  const [quickViewClosing, setQuickViewClosing] = useState(false);
  const quickViewCardRef = useRef<HTMLElement | null>(null);
  const [draggedPlayerId, setDraggedPlayerId] = useState('');
  const [draggedSlot, setDraggedSlot] = useState('');
  const [captainId, setCaptainId] = useState('');
  const [viceCaptainId, setViceCaptainId] = useState('');
  const [announced, setAnnounced] = useState(false);
  const [draftRecovered, setDraftRecovered] = useState(false);
  const [lastLocalSave, setLastLocalSave] = useState('');
  const [showTactics, setShowTactics] = useState(false);
  const [slotPicker, setSlotPicker] = useState<string | null>(null);
  const [slotSearch, setSlotSearch] = useState('');
  const [pointerDraggingSlot, setPointerDraggingSlot] = useState('');
  const pointerDragRef = useRef<{
    playerId: string;
    originSlot: string;
    startX: number;
    startY: number;
    moved: boolean;
    pointerId: number;
  } | null>(null);
  const suppressCardClickRef = useRef(false);

  const sortedPlayers = useMemo(() => sortPlayers(players, matches), [players, matches]);
  const definition = FORMATIONS[formation] || FORMATIONS['4-3-3'];

  const starterIds = useMemo(
    () => new Set(Object.values(slots).filter(Boolean) as string[]),
    [slots],
  );

  const benchPlayers = useMemo(
    () => sortedPlayers.filter((player) => !starterIds.has(player.id)),
    [sortedPlayers, starterIds],
  );

  const starterPlayers = useMemo(
    () =>
      Object.values(slots)
        .filter(Boolean)
        .map((playerId) => sortedPlayers.find((player) => player.id === playerId))
        .filter((player): player is Player => Boolean(player)),
    [slots, sortedPlayers],
  );

  const selectedPlayer = useMemo(
    () => sortedPlayers.find((player) => player.id === selectedPlayerId) || null,
    [selectedPlayerId, sortedPlayers],
  );

  const quickViewPlayer = useMemo(
    () => sortedPlayers.find((player) => player.id === quickViewPlayerId) || null,
    [quickViewPlayerId, sortedPlayers],
  );

  const quickViewStats = useMemo(
    () => quickViewPlayer ? playerStats(matches, quickViewPlayer.id) : null,
    [matches, quickViewPlayer],
  );

  const canSeeInternalPlayerData = canViewSensitiveIntelligence(team?.role);

  const quickViewTopAttributes = useMemo(() => {
    if (!quickViewPlayer || !canSeeInternalPlayerData) return [];
    return Object.entries(quickViewPlayer.attributes ?? {})
      .filter(([, value]) => Number.isFinite(Number(value)))
      .sort((a, b) => Number(b[1]) - Number(a[1]))
      .slice(0, 5);
  }, [quickViewPlayer, canSeeInternalPlayerData]);


  function closeQuickView() {
    if (!quickViewPlayerId || quickViewClosing) return;
    setQuickViewClosing(true);
    window.setTimeout(() => {
      setQuickViewPlayerId('');
      setQuickViewClosing(false);
    }, 190);
  }

  function moveQuickViewCard(event: ReactPointerEvent<HTMLElement>) {
    const card = quickViewCardRef.current;
    if (!card || event.pointerType === 'touch') return;
    const rect = card.getBoundingClientRect();
    const px = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    const py = Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height));
    const rotateY = -7 + (px - 0.5) * 8;
    const rotateX = 1.8 - (py - 0.5) * 6;
    card.style.setProperty('--card-rx', `${rotateX.toFixed(2)}deg`);
    card.style.setProperty('--card-ry', `${rotateY.toFixed(2)}deg`);
    card.style.setProperty('--shine-x', `${Math.round(px * 100)}%`);
    card.style.setProperty('--shine-y', `${Math.round(py * 100)}%`);
  }

  function resetQuickViewCard() {
    const card = quickViewCardRef.current;
    if (!card) return;
    card.style.setProperty('--card-rx', '1.8deg');
    card.style.setProperty('--card-ry', '-7deg');
    card.style.setProperty('--shine-x', '32%');
    card.style.setProperty('--shine-y', '18%');
  }

  const slotPickerPlayers = useMemo(() => {
    const query = slotSearch.trim().toLowerCase();
    return sortedPlayers.filter((player) => {
      if (!query) return true;
      return `${player.name} ${player.position || ''} ${player.squad_number ?? ''}`.toLowerCase().includes(query);
    });
  }, [slotSearch, sortedPlayers]);

  const teamOverall = useMemo(() => {
    if (!starterPlayers.length) return 0;
    const total = starterPlayers.reduce((sum, player) => sum + playerOverall(player), 0);
    return Math.round(total / starterPlayers.length);
  }, [starterPlayers]);

  const averageFitness = useMemo(() => {
    if (!starterPlayers.length) return 0;
    const total = starterPlayers.reduce(
      (sum, player) => sum + Math.max(0, Math.min(100, Number(player.fitness ?? 75))),
      0,
    );
    return Math.round(total / starterPlayers.length);
  }, [starterPlayers]);

  function rowDepthClass(rowIndex: number) {
    const last = Math.max(1, definition.rows.length - 1);
    const ratio = rowIndex / last;
    if (ratio < .2) return 'depth-front';
    if (ratio < .45) return 'depth-midfront';
    if (ratio < .7) return 'depth-midback';
    return 'depth-back';
  }

  const chemistry = useMemo(() => {
    if (starterPlayers.length < 2) return 0;
    const starterSet = new Set(starterPlayers.map((player) => player.id));
    const activeLinks = relationships.filter(
      (relationship) => starterSet.has(relationship.player_a) && starterSet.has(relationship.player_b),
    );
    if (!activeLinks.length) return 0;
    return Math.round(
      activeLinks.reduce((sum, relationship) => sum + Number(relationship.score ?? 0), 0) /
        activeLinks.length,
    );
  }, [relationships, starterPlayers]);

  const pitchPositions = useMemo(() => {
    const worldMap = FORMATION_WORLD_COORDS[formation] || FORMATION_WORLD_COORDS['4-3-3'];
    return definition.rows.flatMap((row, rowIndex) =>
      row.map((slot) => {
        const world = worldMap[slot] || { x: 0, z: 0.5 };
        const projected = projectWorldPoint(world.x, world.z);
        return {
          slot,
          rowIndex,
          rowSize: row.length,
          ...projected,
          y: formationRowY(rowIndex, definition.rows.length),
          player: sortedPlayers.find((candidate) => candidate.id === slots[slot]) || null,
        };
      }),
    );
  }, [definition, formation, slots, sortedPlayers]);

  const chemistryLinks = useMemo(() => {
    const positionByPlayer = new Map(
      pitchPositions
        .filter((position) => position.player)
        .map((position) => [position.player!.id, position]),
    );

    return relationships.flatMap((relationship) => {
      const a = positionByPlayer.get(relationship.player_a);
      const b = positionByPlayer.get(relationship.player_b);
      if (!a || !b) return [];
      return [{
        key: relationship.id || `${relationship.player_a}-${relationship.player_b}`,
        x1: a.x,
        y1: a.y,
        x2: b.x,
        y2: b.y,
        score: Number(relationship.score ?? 0),
      }];
    });
  }, [pitchPositions, relationships]);

  function autoPickStartingXI() {
    const used = new Set<string>();
    const next: Record<string, string | null> = {};
    definition.rows.flat().forEach((slot) => {
      const candidates = sortedPlayers
        .filter((player) => !used.has(player.id))
        .map((player) => ({ player, score: playerOverall(player) + roleAffinity(player, slot) }))
        .sort((a,b) => b.score-a.score);
      const pick = candidates[0]?.player || null;
      next[slot] = pick?.id || null;
      if (pick) used.add(pick.id);
    });
    setSlots(next);
    setSelectedPlayerId('');
    setMessage('Auto Pick applied · best available positional fit');
  }

  useEffect(() => {
    if (!team || draftRecovered) return;
    try {
      const raw = localStorage.getItem(draftStorageKey(team.id));
      if (raw) {
        const draft = JSON.parse(raw) as Partial<LocalLineupDraft>;
        if (draft.name) setName(draft.name);
        if (draft.formation && FORMATIONS[draft.formation]) setFormation(draft.formation);
        if (draft.slots && typeof draft.slots === 'object') setSlots(draft.slots);
        setCaptainId(draft.captainId || '');
        setViceCaptainId(draft.viceCaptainId || '');
        setAnnounced(Boolean(draft.announced));
        setLastLocalSave(draft.savedAt || '');
        setMessage(t.localDraftRecovered);
      }
    } catch {
      // A corrupt local draft must never prevent lineup use.
    } finally {
      setDraftRecovered(true);
    }
  }, [team, draftRecovered, t.localDraftRecovered]);

  useEffect(() => {
    if (!team || !draftRecovered) return;
    const timer = window.setTimeout(() => {
      try {
        const draft: LocalLineupDraft = {
          formation,
          name,
          slots,
          captainId,
          viceCaptainId,
          announced,
          savedAt: new Date().toISOString(),
        };
        localStorage.setItem(draftStorageKey(team.id), JSON.stringify(draft));
        setLastLocalSave(draft.savedAt);
      } catch {
        // Supabase save remains available even if local storage is unavailable.
      }
    }, 350);

    return () => window.clearTimeout(timer);
  }, [team, draftRecovered, formation, name, slots, captainId, viceCaptainId, announced]);

  useEffect(() => {
    if (!team) return;
    try {
      const raw = localStorage.getItem(metaStorageKey(team.id, name));
      if (!raw) {
        setCaptainId('');
        setViceCaptainId('');
        setAnnounced(false);
        return;
      }
      const meta = JSON.parse(raw) as LineupMeta;
      setCaptainId(meta.captainId || '');
      setViceCaptainId(meta.viceCaptainId || '');
      setAnnounced(Boolean(meta.announced));
    } catch {
      setCaptainId('');
      setViceCaptainId('');
      setAnnounced(false);
    }
  }, [team, name]);

  function persistMeta(next?: Partial<LineupMeta>) {
    if (!team) return;
    const meta: LineupMeta = {
      captainId: next?.captainId ?? captainId,
      viceCaptainId: next?.viceCaptainId ?? viceCaptainId,
      announced: next?.announced ?? announced,
    };
    try {
      localStorage.setItem(metaStorageKey(team.id, name), JSON.stringify(meta));
    } catch {
      // Device-local enhancement only; lineup itself still saves to Supabase.
    }
  }

  function clearDuplicate(next: Record<string, string | null>, playerId: string, exceptSlot = '') {
    Object.keys(next).forEach((key) => {
      if (key !== exceptSlot && next[key] === playerId) next[key] = null;
    });
  }

  function placePlayer(slot: string, playerId: string) {
    if (!playerId) {
      setSlots((current) => ({ ...current, [slot]: null }));
      return;
    }

    setSlots((current) => {
      const next = { ...current };
      const previousPlayerAtTarget = next[slot] || null;
      const originSlot = Object.keys(next).find((key) => next[key] === playerId) || '';

      clearDuplicate(next, playerId, slot);
      next[slot] = playerId;

      if (originSlot && originSlot !== slot && previousPlayerAtTarget) {
        next[originSlot] = previousPlayerAtTarget;
      }

      return next;
    });

    setSelectedPlayerId('');
  }

  function handleSlotTap(slot: string) {
    if (selectedPlayerId) {
      placePlayer(slot, selectedPlayerId);
      return;
    }

    const playerId = slots[slot];
    if (playerId) setSelectedPlayerId(playerId);
  }

  function openSlotPicker(slot: string) {
    setSlotPicker(slot);
    setSlotSearch('');
  }

  function beginPointerMove(event: ReactPointerEvent<HTMLElement>, playerId: string, slot: string) {
    if (event.pointerType === 'mouse') return;
    pointerDragRef.current = {
      playerId,
      originSlot: slot,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
      pointerId: event.pointerId,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function updatePointerMove(event: ReactPointerEvent<HTMLElement>) {
    const drag = pointerDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const distance = Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY);
    if (distance > 8) {
      drag.moved = true;
      setPointerDraggingSlot(drag.originSlot);
      event.preventDefault();
    }
  }

  function finishPointerMove(event: ReactPointerEvent<HTMLElement>) {
    const drag = pointerDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    if (drag.moved) {
      const target = document
        .elementFromPoint(event.clientX, event.clientY)
        ?.closest<HTMLElement>('[data-lineup-slot]')
        ?.dataset.lineupSlot;

      if (target && target !== drag.originSlot) {
        placePlayer(target, drag.playerId);
        setMessage(language === 'pl' ? 'Zawodnik przeniesiony na nową pozycję.' : 'Player moved to new position.');
      }
      suppressCardClickRef.current = true;
      window.setTimeout(() => { suppressCardClickRef.current = false; }, 0);
    }

    pointerDragRef.current = null;
    setPointerDraggingSlot('');
  }

  function onPlayerDragStart(playerId: string, slot = '') {
    setDraggedPlayerId(playerId);
    setDraggedSlot(slot);
  }

  function onSlotDrop(slot: string) {
    if (!draggedPlayerId) return;

    setSlots((current) => {
      const next = { ...current };
      const targetPlayer = next[slot] || null;

      clearDuplicate(next, draggedPlayerId, slot);
      next[slot] = draggedPlayerId;

      if (draggedSlot && draggedSlot !== slot) {
        next[draggedSlot] = targetPlayer;
      }

      return next;
    });

    setDraggedPlayerId('');
    setDraggedSlot('');
  }

  function changeFormation(nextFormation: string) {
    const nextDefinition = FORMATIONS[nextFormation];
    if (!nextDefinition) return;

    const currentPlayers = Object.values(slots).filter(Boolean) as string[];
    const nextSlotNames = nextDefinition.rows.flat();
    const nextSlots: Record<string, string | null> = {};

    nextSlotNames.forEach((slot, index) => {
      nextSlots[slot] = currentPlayers[index] || null;
    });

    setFormation(nextFormation);
    setSlots(nextSlots);
    setSelectedPlayerId('');
  }

  function applyPreset(preset: TacticalPreset) {
    changeFormation(preset.formation);
    setMessage(t.presetApplied);
  }

  function resetBoard() {
    setSlots({});
    setCaptainId('');
    setViceCaptainId('');
    setAnnounced(false);
    setSelectedPlayerId('');
    setMessage('');
    if (team) {
      try {
        localStorage.removeItem(draftStorageKey(team.id));
        setLastLocalSave('');
      } catch {
        // Ignore device-storage failure.
      }
    }
  }

  function loadSavedLineup(lineup: Lineup) {
    const nextFormation = lineup.formation && FORMATIONS[lineup.formation]
      ? lineup.formation
      : '4-3-3';

    setName(lineup.name);
    setFormation(nextFormation);
    setSlots((lineup.slots || {}) as Record<string, string | null>);
    setSelectedPlayerId('');
    setMessage(t.savedLineupLoaded);
  }

  async function save() {
    setMessage('');
    const result = await saveLineup({ name, formation, slots });
    persistMeta();
    if (result.ok) {
      setMessage(t.lineupSavedCloudAndDevice);
    } else {
      setMessage(`${result.message} · ${t.localDraftProtected}`);
    }
  }

  async function toggleAnnounced() {
    const next = !announced;
    setMessage(next ? 'Publishing lineup…' : 'Returning lineup to draft…');
    const result = await publishLineup({ name, formation, slots, publish: next });
    if (!result.ok) {
      setMessage(result.message);
      return;
    }
    setAnnounced(next);
    persistMeta({ announced: next });
    setMessage(next ? t.lineupAnnounced : t.lineupReturnedToDraft);
  }

  function setCaptain(playerId: string) {
    const nextVice = viceCaptainId === playerId ? '' : viceCaptainId;
    setCaptainId(playerId);
    setViceCaptainId(nextVice);
    persistMeta({ captainId: playerId, viceCaptainId: nextVice });
  }

  function setViceCaptain(playerId: string) {
    if (captainId === playerId) return;
    setViceCaptainId(playerId);
    persistMeta({ viceCaptainId: playerId });
  }

  if (loading) return <section className="metal-card loading-panel">{t.loading}…</section>;
  if (error) return <section className="metal-card error-panel">{error}</section>;

  return (
    <div className="lineup-page tactical-command-page">
      <section className="metal-card module-heading lineup-controls tactical-command-header">
        <div>
          <p className="eyebrow">TACTICAL COMMAND BOARD</p>
          <h1>{t.lineup}</h1>
          <p className="muted">{t.tacticalBoardSubtitle}</p>
        </div>

        <div className="lineup-control-grid">
          <label>
            <span>{t.lineupName}</span>
            <input value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          <label className="formation-select-control">
            <span>{t.formation}</span>
            <select value={formation} onChange={(event) => changeFormation(event.target.value)}>
              {Object.keys(FORMATIONS).map((key) => <option key={key}>{key}</option>)}
            </select>
          </label>
          <button className="gold-button" onClick={() => void save()}>
            <Save size={18} />
            {t.saveLineup}
          </button>
          <button className={`announce-lineup-button ${announced ? 'announced' : ''}`} onClick={toggleAnnounced}>
            <BadgeCheck size={18} />
            {announced ? t.lineupAnnouncedStatus : t.announceLineup}
          </button>
        </div>

        <div className="tactical-status-strip">
          <span><Users size={16} /> {starterPlayers.length}/11 {t.selectedPlayers}</span>
          <span><Sparkles size={16} /> {t.readiness} {chemistry}/100</span>
          <span className={announced ? 'status-announced' : ''}>
            <BadgeCheck size={16} /> {announced ? t.announced : t.draft}
          </span>
          <span className="local-save-status">
            <Save size={16} /> {lastLocalSave ? t.localBackupActive : t.localBackupPreparing}
          </span>
        </div>

        {message && <p className="form-message">{message}</p>}
      </section>

      {showTactics && (
      <section className="metal-card tactical-presets-panel reference-tactics-panel">
        <div className="section-title-line">
          <div>
            <p className="eyebrow">{t.tacticalPresets}</p>
            <h2>{t.gamePlan}</h2>
          </div>
          <button className="icon-steel-button" type="button" onClick={resetBoard} title={t.resetBoard}>
            <RotateCcw size={17} />
          </button>
        </div>

        <div className="tactical-preset-grid">
          {PRESETS.map((preset) => (
            <button key={preset.id} onClick={() => applyPreset(preset)}>
              <strong>{t[preset.labelKey]}</strong>
              <span>{preset.formation}</span>
              <small>{t[preset.descriptionKey]}</small>
            </button>
          ))}
        </div>
      </section>
      )}


      <section className="stadium-lineup-layout reference-lineup-layout">
        <section className="reference-lineup-header metal-card">
          <div className="reference-club-block">
            <img src={POLONIA_CREST} alt="" />
            <div>
              <small>POLONIA GLASGOW AFC · MATCHDAY READY</small>
              <label className="reference-formation-select">
                <select value={formation} onChange={(event) => changeFormation(event.target.value)}>
                  {Object.keys(FORMATIONS).map((key) => <option key={key}>{key}</option>)}
                </select>
              </label>
            </div>
          </div>
          <div className="reference-metrics">
            <span><small>OVR</small><strong>{teamOverall || '—'}</strong></span>
            <span className="chem"><small>CHEMISTRY</small><strong>{chemistry || '—'}</strong></span>
            <span className="fit"><small>FITNESS</small><strong>{averageFitness ? `${averageFitness}%` : '—'}</strong></span>
          </div>
        </section>

        <section className={`reference-stadium metal-card v181-stadium formation-rows-${definition.rows.length}`}>
          <div className="v181-scene" aria-hidden="true">
            <div className="v181-roof"/>
            <div className="v181-light left"/>
            <div className="v181-light right"/>
            <div className="v181-stand left"/>
            <div className="v181-stand right"/>
            <div className="v181-endstand"/>
            <div className="v181-led left"/>
            <div className="v181-led right"/>
            <div className="v181-pitch">
              <div className="v181-touchline"/>
              <div className="v181-halfway"/>
              <div className="v181-circle"/>
              <div className="v181-box far"/>
              <div className="v181-box near"/>
              <div className="v181-goal far"/>
              <div className="v181-goal near"/>
            </div>
          </div>
          <div className="reference-stadium-screen">
            <img src={POLONIA_CREST} alt="" />
            <strong>POLONIA<br/>GLASGOW AFC</strong>
            <img src={POLONIA_CREST} alt="" />
          </div>

          <svg className="reference-chemistry-lines" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            {chemistryLinks.map((link) => (
              <line
                key={link.key}
                x1={link.x1}
                y1={link.y1}
                x2={link.x2}
                y2={link.y2}
                className={link.score >= 60 ? 'strong' : link.score >= 30 ? 'medium' : 'weak'}
              />
            ))}
          </svg>

          <button type="button" className="reference-side-action tactics" onClick={() => setShowTactics((current) => !current)}>
            <Grip size={21}/><span>TACTICS</span>
          </button>
          <button type="button" className="reference-side-action autopick" onClick={autoPickStartingXI}>
            <Sparkles size={21}/><span>AUTO</span>
          </button>

          <div className="reference-player-layer">
            {pitchPositions.map(({ slot, rowSize, x, y, scale, rotate, shadow, player }) => {
              const selected = Boolean(player && player.id === selectedPlayerId);
              const ovr = player ? playerOverall(player) : null;
              const fitness = player ? Math.round(Number(player.fitness ?? 75)) : 0;
              const form = player ? Number(player.current_form ?? 0) : 0;
              return (
                <div
                  key={slot}
                  data-lineup-slot={slot}
                  className={`reference-slot row-count-${rowSize} ${selected ? 'selected' : ''} ${pointerDraggingSlot === slot ? 'pointer-dragging' : ''}`}
                  style={{
                    left: `${x}%`,
                    top: `${y}%`,
                    ['--card-scale' as any]: scale,
                    ['--card-rotate' as any]: `${rotate}deg`,
                    ['--card-lift' as any]: `${-2 - (1 - scale) * 10}px`,
                    ['--card-shadow' as any]: shadow,
                  }}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={() => onSlotDrop(slot)}
                >
                  <div
                    role="button"
                    tabIndex={0}
                    className={`reference-player-card ${player ? 'occupied' : 'vacant'}`}
                    draggable={Boolean(player)}
                    onDragStart={() => player && onPlayerDragStart(player.id, slot)}
                    onPointerDown={(event) => player && beginPointerMove(event, player.id, slot)}
                    onPointerMove={updatePointerMove}
                    onPointerUp={finishPointerMove}
                    onPointerCancel={finishPointerMove}
                    onClick={() => {
                      if (suppressCardClickRef.current) return;
                      if (selectedPlayerId) {
                        placePlayer(slot, selectedPlayerId);
                      } else if (player) {
                        setQuickViewPlayerId(player.id);
                      } else {
                        openSlotPicker(slot);
                      }
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      if (player) setQuickViewPlayerId(player.id); else openSlotPicker(slot);
                    }}
                  >
                    <span className="reference-card-border" />
                    <span className="reference-card-rating"><b>{ovr ?? '—'}</b><small>{slot}</small></span>
                    <span className="reference-card-photo"><img src={player?.photo_url || POLONIA_CREST} alt="" /></span>
                    <span className="reference-card-number">{player?.squad_number ?? ''}</span>
                    <span className="reference-card-name">{player ? player.name.split(' ').slice(-1)[0] : slot}</span>
                    <span className="reference-card-minirow">
                      <img src={POLONIA_CREST} alt="" />
                      {player && <em className={form >= 7 ? 'positive' : ''}><ArrowUpRight size={9}/>{form.toFixed(1)}</em>}
                    </span>
                    {player ? (
                      <span className="reference-fitness-pips" aria-label={`Fitness ${fitness}%`}>
                        {[0,1,2,3,4].map((i) => <i key={i} className={fitness >= (i+1)*20 ? 'on' : ''}/>) }
                      </span>
                    ) : <span className="reference-card-plus">+</span>}
                    {player?.id === captainId && <span className="captain-badge">C</span>}
                    {player?.id === viceCaptainId && <span className="vice-captain-badge">VC</span>}
                    {player && (
                      <button
                        type="button"
                        className="reference-card-change"
                        title={language === 'pl' ? 'Zmień zawodnika' : 'Change player'}
                        aria-label={`${t.selectPlayer} ${slot}`}
                        onPointerDown={(event) => event.stopPropagation()}
                        onClick={(event) => {
                          event.stopPropagation();
                          openSlotPicker(slot);
                        }}
                      >
                        <UserPlus size={11} />
                      </button>
                    )}
                    {player && <span className="reference-card-move-hint"><Move size={10} /></span>}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {slotPicker && (
          <div className="lineup-picker-backdrop" role="presentation" onMouseDown={() => setSlotPicker(null)}>
            <section
              className="metal-card lineup-slot-picker"
              role="dialog"
              aria-modal="true"
              aria-label={`${t.selectPlayer} ${slotPicker}`}
              onMouseDown={(event) => event.stopPropagation()}
            >
              <header>
                <div>
                  <p className="eyebrow">{slotPicker}</p>
                  <h2>{language === 'pl' ? 'Wybierz zawodnika' : 'Assign player'}</h2>
                </div>
                <button className="icon-steel-button" type="button" onClick={() => setSlotPicker(null)} aria-label={t.close}>
                  <X size={18} />
                </button>
              </header>
              <input
                className="lineup-picker-search"
                autoFocus
                value={slotSearch}
                onChange={(event) => setSlotSearch(event.target.value)}
                placeholder={language === 'pl' ? 'Szukaj zawodnika…' : 'Search player…'}
              />
              <div className="lineup-picker-list">
                <button
                  type="button"
                  className="lineup-picker-player clear-slot"
                  onClick={() => {
                    placePlayer(slotPicker, '');
                    setSlotPicker(null);
                  }}
                >
                  <span className="picker-avatar"><X size={18} /></span>
                  <span><strong>{language === 'pl' ? 'Wyczyść pozycję' : 'Clear position'}</strong><small>{slotPicker}</small></span>
                </button>
                {slotPickerPlayers.map((candidate) => (
                  <button
                    type="button"
                    key={candidate.id}
                    className={`lineup-picker-player ${starterIds.has(candidate.id) ? 'is-starter' : ''}`}
                    onClick={() => {
                      placePlayer(slotPicker, candidate.id);
                      setSlotPicker(null);
                    }}
                  >
                    <span className="picker-avatar">
                      <img src={candidate.photo_url || POLONIA_CREST} alt="" />
                    </span>
                    <span>
                      <strong>{candidate.name}</strong>
                      <small>{candidate.position || '—'} · #{candidate.squad_number ?? '—'}</small>
                    </span>
                    <b>{playerOverall(candidate)}</b>
                  </button>
                ))}
              </div>
            </section>
          </div>
        )}

        <section className="reference-bench metal-card">
          <div className="reference-bench-heading">
            <div><p className="eyebrow">SUBSTITUTES</p><h2>{t.availablePlayers}</h2></div>
            <span>DRAG TO SUBSTITUTE</span>
          </div>
          <div className="reference-bench-track">
            {benchPlayers.map((player) => (
              <button
                type="button"
                key={player.id}
                draggable
                onDragStart={() => onPlayerDragStart(player.id)}
                onClick={() => setSelectedPlayerId((current) => current === player.id ? '' : player.id)}
                className={`reference-bench-card ${selectedPlayerId === player.id ? 'selected' : ''}`}
              >
                <span className="reference-bench-rating">{playerOverall(player)}</span>
                <span className="reference-bench-position">{player.position || '—'}</span>
                <img src={player.photo_url || POLONIA_CREST} alt="" />
                <strong>{player.name.split(' ').slice(-1)[0]}</strong>
                <span className="reference-bench-pips">{[0,1,2,3,4].map((i) => <i key={i} className={Number(player.fitness ?? 75) >= (i+1)*20 ? 'on' : ''}/>)}</span>
              </button>
            ))}
          </div>
        </section>

        {quickViewPlayer && (
          <div className={`lineup-quickview-backdrop ${quickViewClosing ? 'is-closing' : ''}`} role="dialog" aria-modal="true" aria-label={`${quickViewPlayer.name} lineup details`} onClick={closeQuickView}>
            <article
              ref={quickViewCardRef}
              className={`lineup-quickview-card ${quickViewClosing ? 'is-closing' : ''}`}
              onClick={(event) => event.stopPropagation()}
              onPointerMove={moveQuickViewCard}
              onPointerLeave={resetQuickViewCard}
            >
              <div className="lineup-quickview-light-sweep" aria-hidden="true" />
              <button type="button" className="lineup-quickview-close" onClick={closeQuickView} aria-label="Close"><X size={19}/></button>
              <div className="lineup-quickview-crest"><img src={POLONIA_CREST} alt="" /></div>
              {(quickViewPlayer.id === captainId || quickViewPlayer.id === viceCaptainId) && (
                <span className={`lineup-quickview-leader ${quickViewPlayer.id === captainId ? 'captain' : 'vice'}`}>{quickViewPlayer.id === captainId ? 'C' : 'VC'}</span>
              )}
              <div className="lineup-quickview-photo"><img src={quickViewPlayer.photo_url || POLONIA_CREST} alt={quickViewPlayer.name} /></div>
              <div className="lineup-quickview-head">
                <span>#{quickViewPlayer.squad_number ?? '—'} · {quickViewPlayer.position || '—'}</span>
                <h2>{quickViewPlayer.name}</h2>
                {canSeeInternalPlayerData && <strong>OVR {playerOverall(quickViewPlayer)}</strong>}
              </div>
              <section className="lineup-quickview-snapshot">
                <p className="eyebrow">Match snapshot</p>
                <div className="lineup-quickview-stats">
                  <span><small>Form</small><b>{Number(quickViewPlayer.current_form ?? 0).toFixed(1)}</b></span>
                  <span><small>Apps</small><b>{quickViewStats?.apps ?? 0}</b></span>
                  <span><small>Minutes</small><b>{quickViewStats?.minutes ?? 0}</b></span>
                  <span><small>Goals</small><b>{quickViewStats?.goals ?? 0}</b></span>
                  <span><small>Assists</small><b>{quickViewStats?.assists ?? 0}</b></span>
                  <span><small>Avg rating</small><b>{quickViewStats?.ratedMatches ? (quickViewStats.ratingTotal / quickViewStats.ratedMatches).toFixed(1) : '—'}</b></span>
                </div>
                {canSeeInternalPlayerData && (
                  <div className="lineup-quickview-internal-strip">
                    <span><small>Fitness</small><b>{Math.round(Number(quickViewPlayer.fitness ?? 0))}%</b></span>
                    <span><small>Morale</small><b>{Math.round(Number(quickViewPlayer.morale ?? 0))}%</b></span>
                    <span><small>OVR</small><b>{playerOverall(quickViewPlayer)}</b></span>
                  </div>
                )}
              </section>
              {canSeeInternalPlayerData && quickViewTopAttributes.length > 0 && (
                <div className="lineup-quickview-attributes">
                  <p className="eyebrow">Top attributes</p>
                  {quickViewTopAttributes.map(([name, value]) => (
                    <span key={name}><small>{name.replaceAll('_', ' ')}</small><b>{Math.round(Number(value))}</b></span>
                  ))}
                </div>
              )}
              {canSeeInternalPlayerData && starterIds.has(quickViewPlayer.id) && (
                <div className="lineup-quickview-actions">
                  <button type="button" className={captainId === quickViewPlayer.id ? 'active' : ''} onClick={() => setCaptain(quickViewPlayer.id)}><Crown size={15}/> {t.captain}</button>
                  <button type="button" className={viceCaptainId === quickViewPlayer.id ? 'active' : ''} onClick={() => setViceCaptain(quickViewPlayer.id)}><UserCheck size={15}/> {t.viceCaptain}</button>
                </div>
              )}
            </article>
          </div>
        )}

        {selectedPlayer && (
          <div className="lineup-placement-selection">
            <span>{language === 'pl' ? 'Wybrano do ustawienia' : 'Selected for placement'}: <strong>{selectedPlayer.name}</strong></span>
            <button type="button" onClick={() => setSelectedPlayerId('')}><X size={14}/></button>
          </div>
        )}

        <section className="lineup-operations-grid">
          <section className="metal-card leadership-panel">
            <p className="eyebrow">{t.leadership}</p>
            <label>
              <span><Crown size={15} /> {t.captain}</span>
              <select value={captainId} onChange={(event) => setCaptain(event.target.value)}>
                <option value="">—</option>
                {starterPlayers.map((player) => (
                  <option key={player.id} value={player.id}>#{player.squad_number ?? '—'} {player.name}</option>
                ))}
              </select>
            </label>
            <label>
              <span><UserCheck size={15} /> {t.viceCaptain}</span>
              <select value={viceCaptainId} onChange={(event) => setViceCaptain(event.target.value)}>
                <option value="">—</option>
                {starterPlayers
                  .filter((player) => player.id !== captainId)
                  .map((player) => (
                    <option key={player.id} value={player.id}>#{player.squad_number ?? '—'} {player.name}</option>
                  ))}
              </select>
            </label>
          </section>

          <section className="metal-card saved-lineups-panel saved-lineups-interactive">
            <p className="eyebrow">{t.savedLineups}</p>
            {lineups.slice(0, 8).map((lineup) => (
              <button className="saved-lineup-row" key={lineup.id} onClick={() => loadSavedLineup(lineup)}>
                <Shield size={17} />
                <div>
                  <strong>{lineup.name}</strong>
                  <span>{lineup.formation || '—'}</span>
                </div>
              </button>
            ))}
            {!lineups.length && <p className="muted">{t.noSavedLineups}</p>}
          </section>
        </section>
      </section>

      <MatchdayMediaStudio
        team={team}
        players={players}
        matches={matches}
        lineups={lineups}
        currentFormation={formation}
        currentSlots={slots}
      />
    </div>
  );
}